import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FileUploadModule } from '@vectorsolv/vectors-ui/file-input';
import { StorageItem } from '@vsolv/packages/storage/domain';
import { UtilsModule } from '@vsolv/ui-kit/utils';
import { AlertModule, ToastService } from '@vsolv/vectors-ui/alert';
import { BadgeModule } from '@vsolv/vectors-ui/badge';
import { ButtonModule } from '@vsolv/vectors-ui/button';
import { DialogComponent, DialogConfig, DialogHeaderConfig, DialogModule } from '@vsolv/vectors-ui/dialog';
import { FormsModule } from '@vsolv/vectors-ui/forms';
import { IconsModule } from '@vsolv/vectors-ui/icons';
import { InputModule } from '@vsolv/vectors-ui/input';
import { ThemingModule } from '@vsolv/vectors-ui/theming';
import { map, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { StorageSecurityService, StorageService } from '../../services';

@Component({
  standalone: true,
  selector: 'vs-add-document-dialog',
  templateUrl: './add-document.dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    InputModule,
    IconsModule,
    AlertModule,
    UtilsModule,
    BadgeModule,
    CommonModule,
    DialogModule,
    ButtonModule,
    ThemingModule,
    ReactiveFormsModule,
    FileUploadModule,
  ],
})
export class AddDocumentDialog implements AfterViewInit, OnDestroy {
  private toastSvc = inject(ToastService);
  private storageSvc = inject(StorageService);
  private securitySvc = inject(StorageSecurityService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  private onDestroy$ = new Subject<void>();

  canCreateRuns$ = this.securitySvc.canCreateProcessorRun$;
  @Input() canUpload$ = this.securitySvc.canUpload$;

  @Input() customUploadEndpoint?: string | null;
  @Input() hideUploadAndRunButton?: boolean | null;

  @ViewChild(DialogComponent) private instance?: DialogComponent;

  protected dialogConfig: DialogConfig = { dialogContainerClass: '!max-w-[80vw]' };
  protected dialogHeaderConfig: DialogHeaderConfig = {
    mode: 'simple',
    showDivider: true,
    iconTheme: 'primary',
    title: 'Add document',
    icon: 'upload-cloud-01',
    subtitle: 'Upload and safely store a document',
  };

  protected form = new FormGroup({
    title: new FormControl<string | null>(null, [Validators.required, Validators.minLength(1)]),
    description: new FormControl<string | null>(null),
    file: new FormControl<null | File>(null, Validators.required),
  });

  protected uploading = false;

  protected imageResourceUrl$ = this.form.controls.file.valueChanges.pipe(
    startWith(null),
    switchMap(async () => {
      const file = this.form.value.file;
      if (!file) return null;

      const base64 = await new Promise<string | null>(resolve => {
        const reader = new FileReader();
        reader.onload = e => resolve(<string>e.target?.result || null);
        reader.readAsDataURL(file);
      });

      if (!base64) return null;
      return await new Promise<string | null>(resolve => {
        const image = new Image();
        image.onerror = () => resolve(null);
        image.onload = () => resolve(base64);
        image.src = base64;
      });
    })
  );

  protected fileExtension$ = this.form.valueChanges.pipe(
    startWith(null),
    map(() => (this.form.value.file ? StorageItem.getFileExtension(this.form.value.file.name) : null))
  );

  @Output() closed = new EventEmitter<StorageItem.UploadCommandResponse | null>();
  @Output() opened = new EventEmitter();

  ngAfterViewInit() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.instance?.closed.pipe(takeUntil(this.onDestroy$)).subscribe(data => this.closed.emit((data ?? null) as any));
    this.instance?.opened.pipe(takeUntil(this.onDestroy$)).subscribe(() => this.opened.emit());
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  open() {
    this.reset();
    this.instance?.open();
    this.changeDetectorRef.markForCheck();
  }

  close(data: StorageItem.UploadCommandResponse | null = null) {
    this.instance?.close(data);
    this.changeDetectorRef.markForCheck();
  }

  reset() {
    this.uploading = false;
    this.dialogConfig = { ...this.dialogConfig, disableBackdropDismiss: false };
    this.dialogHeaderConfig = { ...this.dialogHeaderConfig, hideCloseButton: false };
    this.form.reset({ title: null, description: null, file: null });
  }

  protected async upload() {
    const value = this.form.value;
    if (!value.title || !value.file) return;

    this.form.disable();

    this.uploading = true;
    this.dialogConfig = { ...this.dialogConfig, disableBackdropDismiss: true };
    this.dialogHeaderConfig = { ...this.dialogHeaderConfig, hideCloseButton: true };

    try {
      await new Promise(res => setTimeout(res, 1000));
      const response = await this.storageSvc.uploadFile(
        {
          description: value.description,
          title: value.title,
          file: value.file,
        },
        this.customUploadEndpoint
      );

      this.close(response);
      this.success(value.title);
    } catch (err) {
      console.error(err);
      this.failure(value.title);
    }

    this.uploading = false;
    this.dialogConfig = { ...this.dialogConfig, disableBackdropDismiss: false };
    this.dialogHeaderConfig = { ...this.dialogHeaderConfig, hideCloseButton: false };

    this.form.enable();

    this.changeDetectorRef.markForCheck();
  }

  private success(title: string) {
    this.toastSvc.show({
      type: 'success',
      title: 'Document uploaded',
      text: `New document '${title}' has been successfully uploaded.`,
    });
  }

  private failure(title: string) {
    this.toastSvc.show({
      type: 'error',
      title: 'Upload failed',
      text: `Failed to upload document '${title}'. Please try again.`,
    });
  }
}
